import { ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'

import { QleDetails } from 'components/QleDetails'
import { Banner, Button, TextLink } from 'slab'
import { usePostMemberOnboardingEnrollmentEvents } from 'utils/web_api'
import { type PostMemberOnboardingEnrollmentEventsEnrollmentSubtype } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { ProfileMenu } from '../components/ProfileMenu'
import { QLEButtonLayout } from '../components/QLEButtonLayout'
import { StubbedOutOnboardingSideBar } from '../components/StubbedOutOnboardingSideBar'

type Props = {
  memberName?: string;
  /** Blocked from QLE because member already has confirmed Medicare for plan year (can't be changed on Thatch) */
  blockedDueToMedicareEnrollment?: boolean;
}

// This page displays when "Change plans" is clicked from the member homepage in order to validate the QLE date and
// create the QLE enrollment event.
// Once the enrollment event is created, the user is redirected to the renewals flow instead of this page.
export const QleStartPage = ({ memberName, blockedDueToMedicareEnrollment }: Props) => {
  const { assign } = useWindowLocation()
  const createQleEnrollmentEvent = usePostMemberOnboardingEnrollmentEvents()

  useEffect(() => {
    window.segment?.page('[EE] QLE start page')
  }, [])

  const handleNext = async ({ qleType, qleDate }: { qleType: PostMemberOnboardingEnrollmentEventsEnrollmentSubtype, qleDate: Date }) => {
    const formattedDate = qleDate.toISOString().split('T')[0]

    const enrollmentEvent = await createQleEnrollmentEvent.mutateAsync({
      data: {
        enrollment_type: 'special_enrollment',
        enrollment_subtype: qleType,
        provided_qle_event_date: formattedDate,
      },
    })
    window.segment?.track('[EE] QLE started', {
      enrollment_event_id: enrollmentEvent.id,
      qle_subtype: qleType,
      qle_date: formattedDate,
    })
    assign('/member_onboarding/progress')
  }

  return (
    <div className='flex bg-white h-screen overflow-hidden isolate'>
      <aside className='hidden xl:flex flex-col flex-shrink-0 p-10 gap-y-10'>
        <ProfileMenu memberName={memberName} withoutContext />
        <div className='pl-3 flex-1'>
          <StubbedOutOnboardingSideBar
            enrollmentEvent={{
              type: 'special_enrollment',
            }} // we don't have the created enrollment event yet
            newQleEnabled
            completed={false}
            activeStep='first'
          />
        </div>
      </aside>
      <main className='flex-1 min-w-0 overflow-y-auto'>
        <div className='flex flex-col gap-y-8 pt-[110px] pb-[100px] px-6 md:px-12 xl:px-[100px]'>
          {blockedDueToMedicareEnrollment
            ? (
              <div className='max-w-[44rem]'>
                <Banner title="Your Medicare coverage can't be changed on Thatch" type='warning'>
                  <p>
                    If you need to make a different change, like getting coverage
                    for a dependent or correcting your expected costs, you can
                    reach out to{' '}
                    <TextLink href='mailto:support@thatch.com' target='_blank'>
                      support@thatch.com
                    </TextLink>{' '}
                    for help.
                  </p>
                </Banner>
              </div>
              )
            : (
              <QleDetails
                onSubmit={handleNext}
                renderButtonLayout={({ submit, disabled }) => (
                  <div className='mt-10'>
                    <QLEButtonLayout
                      backButton={null}
                      rightButtons={
                        <Button
                          rightIcon={ArrowRightIcon}
                          variant='primary'
                          type='button'
                          disabled={disabled}
                          onClick={submit}
                        >
                          Next
                        </Button>
                      }
                      actionMenuContent={
                        <Button
                          variant='secondary'
                          onClick={() => {
                            window.segment?.track('[EE] QLE cancelled', {
                              source: 'qle_start_page',
                            })
                            assign('/')
                          }}
                        >
                          Cancel change
                        </Button>
                      }
                    />
                  </div>
                )}
              />
              )}
        </div>
      </main>
    </div>
  )
}

export default QleStartPage
